/**
 * The Billing page's rules that do not need a canvas: when a time entry starts, which tickets are
 * holding work back, what the timer and the rate rules say.
 *
 * Ported from `BillingViewModel`; kept here so the date arithmetic - the part that goes wrong a day
 * at a time for readers west of UTC - is under test.
 */
import type { BillingConfigDto, PurchaseDto, TicketDto, TimeEntryDto, WorkTimerDto } from './contracts.gen.js';
import { money } from './format.js';
import { BILLING_RULE, isBillable, isFinished, statusLabel } from './rules.js';

/** `HH:mm` (or `H:mm`) as minutes past midnight; null for anything else. */
export function parseTimeOfDay(text: string): number | null {
  const m = /^(\d{1,2}):(\d{2})$/.exec(text.trim());
  if (!m) return null;
  const h = Number(m[1]);
  const min = Number(m[2]);
  return h <= 23 && min <= 59 ? h * 60 + min : null;
}

/**
 * A local calendar day plus an optional local start time, as a UTC instant.
 *
 * No time means LOCAL NOON, so the day cannot shift in any timezone - the rule the C# `BuildStartUtc`
 * follows. A time that is typed but not a time is `undefined`, so the caller can refuse it rather than
 * quietly logging the work at noon.
 */
export function startUtc(dateIso: string, timeText: string): string | null | undefined {
  const d = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateIso);
  if (!d) return null;
  let minutes = 12 * 60;
  if (timeText.trim()) {
    const parsed = parseTimeOfDay(timeText);
    if (parsed === null) return undefined;
    minutes = parsed;
  }
  return new Date(Number(d[1]), Number(d[2]) - 1, Number(d[3]), Math.floor(minutes / 60), minutes % 60).toISOString();
}

/** The local time of an instant as `HH:mm`, for loading an entry back into the editor. */
export function localTimeOfDay(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

/** Open tickets first, then the most recently touched - closed ones stay, because work is written up late. */
export function sortTickets(tickets: readonly TicketDto[]): TicketDto[] {
  return [...tickets].sort(
    (a, b) =>
      Number(isFinished(a.status)) - Number(isFinished(b.status)) || Date.parse(b.updatedUtc) - Date.parse(a.updatedUtc)
  );
}

export function ticketLabel(t: TicketDto): string {
  return `#${t.id} · ${t.title} · ${statusLabel(t.status)}`;
}

/**
 * Which tickets are sitting on unbilled work that cannot be invoiced yet, as a standing sentence -
 * not only in the answer to a Generate that has already come back empty. Blank when nothing is held.
 *
 * A 0-minute entry is not work: the generator skips it (`Minutes > 0`), so counting it here had the
 * page say work was waiting on a ticket while Generate answered "Nothing unbilled to invoice".
 */
export function heldWorkHint(
  entries: readonly TimeEntryDto[],
  purchases: readonly PurchaseDto[],
  tickets: readonly TicketDto[]
): string {
  const held = [
    ...entries.filter(t => t.billable && t.invoiceId === null && t.minutes > 0 && !isBillable(t.ticketStatus)).map(t => t.ticketId),
    ...purchases.filter(p => p.billable && p.invoiceId === null && !isBillable(p.ticketStatus)).map(p => p.ticketId)
  ];
  const ids = [...new Set(held)].sort((a, b) => a - b);
  if (ids.length === 0) return '';
  const shown = 5;
  const named = ids.slice(0, shown).map(id => {
    const t = tickets.find(x => x.id === id);
    return t ? `#${t.id} ${t.title} (${statusLabel(t.status)})` : `#${id}`;
  });
  const more = ids.length > shown ? `, and ${ids.length - shown} more` : '';
  return `${BILLING_RULE} Unbilled work is waiting on ${named.join('; ')}${more}.`;
}

/** What the timer card says. A timer belongs to the person, not the customer, so it says whose. */
export function timerLabel(timer: WorkTimerDto | null, customerId: number): string {
  if (!timer) return 'No timer running';
  const note = timer.notes?.trim() ? timer.notes : '(no note)';
  const where = timer.customerId === customerId ? `#${timer.ticketId} ${timer.ticketTitle}` : `#${timer.ticketId} ${timer.ticketTitle}, for another customer`;
  return `${timer.elapsedMinutes} min on ${where} - ${note}`;
}

export function rateRulesLines(c: BillingConfigDto): [string, string] {
  return [
    `Standard ${money(c.standardRate)}/hr · after-hours ×${c.afterHoursMultiplier} · weekend ×${c.weekendMultiplier}`,
    `Increment ${c.billingIncrementMinutes} min · minimum ${c.minimumMinutes} min · tax ${c.taxPercent}% · purchase markup ${c.purchaseMarkupPercent}%`
  ];
}

/** One piece of billable work no invoice has taken yet - a time entry or a parts charge. */
export interface UninvoicedItem {
  key: string;
  kind: 'time' | 'part';
  ticketId: number;
  ticketTitle: string;
  dateUtc: string;
  description: string;
  detail: string;
  amount: number;
  /** The ticket is resolved or closed, so the next invoice takes it. */
  ready: boolean;
  holdLabel: string;
}

const round2 = (v: number) => Math.round((v + Number.EPSILON) * 100) / 100;

/**
 * What the next invoice would bill, by the generator's own rules: billable, on no invoice, and for
 * time more than 0 minutes. A part with no fixed price is priced at cost plus the customer's markup,
 * as the invoice prices it. Oldest first, the order the invoice prints them in within a ticket.
 */
export function uninvoicedItems(
  entries: readonly TimeEntryDto[],
  purchases: readonly PurchaseDto[],
  markupPercent: number
): UninvoicedItem[] {
  const items: UninvoicedItem[] = [
    ...entries
      .filter(t => t.billable && t.invoiceId === null && t.minutes > 0)
      .map(t => ({
        key: `t${t.id}`,
        kind: 'time' as const,
        ticketId: t.ticketId,
        ticketTitle: t.ticketTitle,
        dateUtc: t.startUtc,
        description: t.notes?.trim() ? t.notes.trim() : 'Labor',
        detail: `${t.minutes} min @ ${money(t.rate)}/hr · ${t.userName}`,
        amount: t.amount,
        ready: isBillable(t.ticketStatus),
        holdLabel: t.holdLabel ?? ''
      })),
    ...purchases
      .filter(p => p.billable && p.invoiceId === null)
      .map(p => {
        const unit = p.unitPrice ?? round2(p.unitCost * (1 + markupPercent / 100));
        return {
          key: `p${p.id}`,
          kind: 'part' as const,
          ticketId: p.ticketId,
          ticketTitle: p.ticketTitle,
          dateUtc: p.purchaseDateUtc,
          description: p.description,
          detail: `${p.quantity} × ${money(unit)}${p.unitPrice === null ? ' (cost + markup)' : ''}`,
          amount: round2(unit * p.quantity),
          ready: isBillable(p.ticketStatus),
          holdLabel: p.holdLabel ?? ''
        };
      })
  ];
  return items.sort((a, b) => a.dateUtc.localeCompare(b.dateUtc));
}

/** The section's headline numbers: what the next invoice would bill, and what is waiting on tickets. */
export function uninvoicedTotals(items: readonly UninvoicedItem[]): {
  ready: number;
  readyCount: number;
  held: number;
  heldCount: number;
} {
  const ready = items.filter(i => i.ready);
  const held = items.filter(i => !i.ready);
  const sum = (xs: UninvoicedItem[]) => round2(xs.reduce((s, i) => s + i.amount, 0));
  return { ready: sum(ready), readyCount: ready.length, held: sum(held), heldCount: held.length };
}

/** "Day 5 of each month · next 2026-11-05" - the billing date as a calendar day, never converted. */
export function autoBillLine(enabled: boolean, day: number, nextRunUtc: string | null): string {
  if (!enabled) return 'Off - invoices for this customer are raised by hand.';
  const next = nextRunUtc ? ` · next run ${nextRunUtc.slice(0, 10)}` : '';
  return `On - day ${day} of each month${next}.`;
}
